import { computed } from 'nanostores';
import { persistentAtom } from '@nanostores/persistent';
import { STUDENT_GROUPS } from '@academy/skilljar/userData/constants';
import { clearLastViewedResource } from './last-viewed-resource';

import type { ISkilljarUser } from '../types';

const initialUserData: ISkilljarUser = {
  email: '',
  firstName: undefined,
  id: '',
  lastName: undefined,
  name: '',
  studentGroups: [
    {
      id: STUDENT_GROUPS.CUSTOMER.ID,
      name: STUDENT_GROUPS.CUSTOMER.NAME,
    },
  ],
};

export const $skilljarUser = persistentAtom<ISkilljarUser>('skilljarUser', initialUserData, {
  encode: JSON.stringify,
  decode: JSON.parse,
});

export const $loggedIn = computed($skilljarUser, (user) => (
  // Check if the user object has a valid id to determine if the user is logged in
  // This assumes that a valid user will always have an id
  !!user && !!user.id
));

// Default user roles when not logged in or no groups
const DEFAULT_USER_ROLES = {
  isCustomer: false,
  isKlaviyo: false,
  isPartner: false,
};

// Unified user roles store
export const $userRoles = computed($skilljarUser, (user) => {
  if (!user || !user.id || !Array.isArray(user.studentGroups)) {
    return DEFAULT_USER_ROLES;
  }

  const partnerGroupIdsFromEnv = (import.meta.env.PUBLIC_SKILLJAR_GROUP_PARTNER_IDS ?? '')
    .replaceAll(' ', '')
    .split(',')
    .filter(Boolean);
  const partnerGroupIdSet = new Set(partnerGroupIdsFromEnv);

  return user.studentGroups.reduce((userRolesObject, studentGroup) => {
    if (partnerGroupIdSet.has(studentGroup.id)) {
      userRolesObject.isPartner = true;
    }
    if (studentGroup.id === STUDENT_GROUPS.KLAVIYO.ID) {
      userRolesObject.isKlaviyo = true;
    }
    if (studentGroup.id === STUDENT_GROUPS.CUSTOMER.ID) {
      userRolesObject.isCustomer = true;
    }
  
    return userRolesObject;
  }, { ...DEFAULT_USER_ROLES });
});

// Backward compatibility - individual computed stores derived from unified store
export const $isCustomer = computed($userRoles, (userRoles) => userRoles.isCustomer);
export const $isKlaviyo = computed($userRoles, (userRoles) => userRoles.isKlaviyo);
export const $isPartner = computed($userRoles, (userRoles) => userRoles.isPartner);

// Listen for user logout and clear last viewed resource
$skilljarUser.subscribe((user) => {
  // If user becomes null, undefined, or has no ID, they have logged out
  if (!user || !user.id) {
    clearLastViewedResource();
  }
});
